import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useApolloClient } from '@apollo/client';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { Typography } from 'common/components/Typography';
import { AddButton } from 'common/components/Button/AddButton';
import { ReceivedCertificateListItem } from 'common/components/ReceivedCertificateListItem';
import {
    IBasicUserFragment,
    IRole,
    useCurrentUserQuery,
    useReceivedCertificatesQuery,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { BoxLoader } from 'common/components/Loader';
import { CertificateGenerateDialog } from 'common/components/CertificateGenerateDialog';
import { Divider } from 'common/components/Divider';
import { useCanManageUser } from 'user/hooks';
import { useRouteMatch } from 'route/hooks/useRouteMatch';

interface IProps extends IPageDrawerProps {
    certificateId: string;
    title?: string;
    user?: IBasicUserFragment;
    onClose?(): void;
    onDeleteCertificate?(): void;
    onGenerateCertificate?(): void;
}

export const ReceivedCertificateDrawer = ({
    certificateId,
    open,
    title,
    user,
    onClose,
    onDeleteCertificate,
    onGenerateCertificate,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const { showApolloError } = useApolloError();
    const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
    const { data: currentUserData, loading: loadingCurrentUser } =
        useCurrentUserQuery();

    const isSkillMatrixPage = !!useRouteMatch('SKILLS_MATRIX');

    const currentUser = currentUserData?.currentUser;
    const currentUserIsManager = currentUser?.roles?.includes(IRole.Manager);

    const skipCanManageUser =
        !user?.id || loadingCurrentUser || currentUserIsManager;
    const { isGroupManager: isGroupManagerOfUser } = useCanManageUser(
        user?.id,
        skipCanManageUser
    );

    const userId = user?.id;

    const isCurrentUser = currentUser?.id === userId;

    const {
        data,
        loading: receivedCertificatesLoading,
        refetch,
    } = useReceivedCertificatesQuery({
        variables: { userId, certificateId },
        skip: !userId,
        onError: showApolloError,
        fetchPolicy: 'network-only',
    });

    const receivedCertificates =
        data?.receivedCertificates?.edges
            .map((edge) => edge?.node)
            .filter(Boolean) || [];

    const refetchQueries = () => {
        refetch();

        client.cache.evict({ fieldName: 'skillPortfolioReport' });
        client.cache.gc();
    };

    const handleGenerateCertificate = () => {
        onGenerateCertificate?.();

        refetchQueries();
    };

    const handleDeleteCertificate = () => {
        onDeleteCertificate?.();

        refetchQueries();
    };

    const canAddOwnCertificate = isCurrentUser && currentUserIsManager;
    const canAddUserCertificate =
        !isCurrentUser && (isGroupManagerOfUser || currentUserIsManager);

    // Current user can generate a certificate when they are a manager
    // When viewing another user's certificates the group managers and managers can generate a certificate
    // When viewing on the skills matrix page
    const canGenerateCertificate =
        !loadingCurrentUser &&
        (canAddOwnCertificate || canAddUserCertificate) &&
        isSkillMatrixPage;

    return (
        <PageDrawer open={open} onClose={onClose}>
            <Box p={4}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h3">{title}</Typography>
                </Box>

                {canGenerateCertificate && (
                    <Box sx={{ mb: 2 }}>
                        <AddButton
                            disabled={receivedCertificatesLoading}
                            onClick={() => setCertificateDialogOpen(true)}
                        >
                            <Typography>
                                {translate(
                                    'receivedCertificateDrawer.addButton'
                                )}
                            </Typography>
                        </AddButton>
                    </Box>
                )}

                <Divider sx={{ mb: 1 }} />

                {receivedCertificatesLoading && <BoxLoader />}

                {!receivedCertificatesLoading &&
                    !receivedCertificates.length && (
                        <Typography sx={{ mt: 4 }}>
                            {translate(
                                'receivedCertificateDrawer.noReceivedCertificates'
                            )}
                        </Typography>
                    )}

                {!receivedCertificatesLoading &&
                    !!userId &&
                    receivedCertificates.map((receivedCertificate) => (
                        <ReceivedCertificateListItem
                            key={receivedCertificate.id}
                            receivedCertificate={receivedCertificate}
                            userId={userId}
                            onDelete={handleDeleteCertificate}
                        />
                    ))}
            </Box>

            {!!userId && canGenerateCertificate && (
                <CertificateGenerateDialog
                    certificateId={certificateId}
                    confirmText={translate(
                        'receivedCertificateDrawer.certificateGenerateDialog.confirmText'
                    )}
                    open={certificateDialogOpen}
                    text={translate(
                        'receivedCertificateDrawer.certificateGenerateDialog.text',
                        {
                            userName: user?.name,
                        }
                    )}
                    title={translate(
                        'receivedCertificateDrawer.certificateGenerateDialog.title'
                    )}
                    userIds={[userId]}
                    onClose={() => {
                        setCertificateDialogOpen(false);
                    }}
                    onGenerateCertificate={handleGenerateCertificate}
                />
            )}
        </PageDrawer>
    );
};
